// Getting credits, from anywhere: a paid button that cannot be covered opens this instead of an
// error, and the sidebar meter opens it to top up ahead of time. It sells whole packs on the
// card on file (as many as the shortfall takes) and, on a trial, an early start of the plan.
import { CREDIT_PACK, TRIAL_REEL, reelPrice } from '@str/shared';
import { Overlay } from '@/components/Overlay';
import { useState } from 'react';
import { useModalDialog } from '@/lib/use-modal-dialog';

type Action = 'buy_pack' | 'start_plan';

/** The reel price quoted here is the one signup quotes and the API spends. */
const REEL_SECONDS = TRIAL_REEL.durationSeconds;
const REEL = reelPrice(REEL_SECONDS);

export function CreditsSheet({
  balance,
  needed,
  actions,
  startCharge,
  then,
  total,
  card,
  busy,
  error = null,
  onBuyPack,
  onStartPlan,
  onClose,
}: {
  balance: number;
  /** What the action that opened the sheet costs; absent when the host just wants more. */
  needed?: number;
  actions: readonly Action[];
  /** "$194 today for Starter annual": what starting the plan now charges. */
  startCharge?: string;
  /** "build the storyboard": what runs by itself once the pack lands, said on the button. */
  then?: string;
  /** The whole job the shortfall belongs to (a 15 s reel: `reelPrice(15).total`), so the sheet can say a pack covers it. */
  total?: number;
  /** "Visa ···· 4242", or null when no card is known here. */
  card: string | null;
  busy: boolean;
  error?: string | null;
  onBuyPack: (packs: number) => void;
  onStartPlan: () => void;
  onClose: () => void;
}) {
  const [ref, onKeyDown] = useModalDialog(onClose);
  const [confirming, setConfirming] = useState(false);
  const shortBy = needed ? Math.max(0, needed - balance) : 0;
  const packs = Math.max(1, Math.ceil(shortBy / CREDIT_PACK.credits));
  const credits = packs * CREDIT_PACK.credits;
  const price = (packs * CREDIT_PACK.priceCents) / 100;
  const s = (n: number) => (n === 1 ? '' : 's');
  // On the body, above whatever opened it: the sidebar and the reel stage each make their own
  // stacking context, and a sheet rendered inside one sits under the other.
  return (
    <Overlay className="nx-sheet-back" onClose={onClose}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="Get credits"
        tabIndex={-1}
        className="nx-sheet credits-sheet"
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="rf-kick">Credits</span>
        <h2>
          You have {balance} credit{s(balance)}.{needed ? ` This needs ${needed}.` : ''}
        </h2>
        <p className="nx-note">
          {`Credits pay for what is made: a ${REEL_SECONDS}-second reel is ${REEL.total} (${REEL.storyboard} for the storyboard, ${REEL.filming} to film it), each new picture or carousel card 1 to 3. Your plan adds more every month; packs are extra and never expire.`}
        </p>
        {confirming ? (
          <div className="credits-sheet-row">
            <span>Your card is charged {startCharge}. Your monthly credits land now.</span>
            <div className="nx-chips">
              <button
                type="button"
                className="btn btn-primary hv"
                disabled={busy}
                onClick={() => {
                  setConfirming(false);
                  onStartPlan();
                }}
              >
                Yes, start my plan
              </button>
              <button type="button" className="nx-quiet" onClick={() => setConfirming(false)}>
                Back
              </button>
            </div>
          </div>
        ) : (
          <>
            {actions.includes('buy_pack') && (
              <div className="credits-sheet-row">
                <button
                  type="button"
                  className="btn btn-primary hv"
                  disabled={busy}
                  onClick={() => onBuyPack(packs)}
                >
                  {busy
                    ? 'Adding them…'
                    : `Buy ${credits} credits · $${price}${then ? `, then ${then}` : ''}`}
                </button>
                <span className="nx-note">
                  {card ? `Charged to ${card}` : 'Charged to your card on file'}
                  {total && balance + credits >= total
                    ? ` · enough for the whole reel, with ${balance + credits - total} left`
                    : ''}
                </span>
              </div>
            )}
            {actions.includes('start_plan') && startCharge && (
              <div className="credits-sheet-row">
                <button
                  type="button"
                  className="btn btn-outline hv"
                  disabled={busy}
                  onClick={() => setConfirming(true)}
                >
                  Start my plan now · {startCharge.split(' today')[0]} today
                </button>
                <span className="nx-note">
                  Ends the trial early; your monthly credits land today.
                </span>
              </div>
            )}
            <button type="button" className="nx-quiet" onClick={onClose}>
              Not now
            </button>
          </>
        )}
        {error && (
          <p role="alert" className="nx-note" style={{ color: '#b3261e' }}>
            {error}
          </p>
        )}
      </div>
    </Overlay>
  );
}
